import { useCallback, useLayoutEffect, useState, type RefObject } from "react";

/** Posicionamento simples de elementos flutuantes (tooltip, menu) — sem dependências. */
export type Placement = "top" | "bottom" | "left" | "right" | "bottom-start" | "bottom-end" | "top-start" | "top-end";

const MARGIN = 8;

export function computePosition(anchor: DOMRect, size: { width: number; height: number }, placement: Placement, offset = 6) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const [side, align] = placement.split("-") as ["top" | "bottom" | "left" | "right", "start" | "end" | undefined];

  let finalSide = side;
  if (side === "top" && anchor.top - size.height - offset < MARGIN) finalSide = "bottom";
  else if (side === "bottom" && anchor.bottom + size.height + offset > vh - MARGIN && anchor.top > size.height + offset) finalSide = "top";
  else if (side === "left" && anchor.left - size.width - offset < MARGIN) finalSide = "right";
  else if (side === "right" && anchor.right + size.width + offset > vw - MARGIN) finalSide = "left";

  let top: number;
  let left: number;
  if (finalSide === "top" || finalSide === "bottom") {
    top = finalSide === "top" ? anchor.top - size.height - offset : anchor.bottom + offset;
    left =
      align === "start"
        ? anchor.left
        : align === "end"
          ? anchor.right - size.width
          : anchor.left + anchor.width / 2 - size.width / 2;
  } else {
    left = finalSide === "left" ? anchor.left - size.width - offset : anchor.right + offset;
    top = anchor.top + anchor.height / 2 - size.height / 2;
  }
  left = Math.min(Math.max(MARGIN, left), vw - size.width - MARGIN);
  top = Math.min(Math.max(MARGIN, top), vh - size.height - MARGIN);
  return { top, left, side: finalSide };
}

/**
 * Mantém `floatingRef` posicionado (position: fixed) junto de `anchorRef` enquanto `open`.
 * Recalcula em scroll (captura) e resize.
 */
export function useFloatingPosition(
  open: boolean,
  anchorRef: RefObject<HTMLElement | null>,
  floatingRef: RefObject<HTMLElement | null>,
  placement: Placement,
  offset = 6,
) {
  const [pos, setPos] = useState<{ top: number; left: number; side: string } | null>(null);

  const update = useCallback(() => {
    const a = anchorRef.current;
    const f = floatingRef.current;
    if (!a || !f) return;
    const rect = f.getBoundingClientRect();
    setPos(computePosition(a.getBoundingClientRect(), { width: rect.width, height: rect.height }, placement, offset));
  }, [anchorRef, floatingRef, placement, offset]);

  useLayoutEffect(() => {
    if (!open) return;
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, update]);

  return open ? pos : null;
}
